// src/pages/projects/ProjectTeam.jsx
import React, { useEffect } from 'react';
import { Link, useParams, useOutletContext } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Alert,
  AlertTitle,
  CircularProgress
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

import useProjectStore from '../../stores/projectStore';
import useAuthStore from '../../stores/authStore';
import useUserStore from '../../stores/userStore';
import useConfirmDialog from '../../hooks/useConfirmationDialog';
import useTeamMemberDialog from './hooks/useTeamMemberDialog';

import { EmptyState } from '../../design-system/components/core';
import TeamMembersList from './components/TeamMembersList';
import TeamMemberDialog from './components/TeamMemberDialog';

const ProjectTeam = () => {
  // Get project from outlet context
  const { project, canEdit } = useOutletContext();
  
  const { id } = useParams();
  const { user } = useAuthStore();
  
  // Store hooks
  const { 
    addProjectTeamMember,
    updateProjectTeamMember,
    removeProjectTeamMember,
    isLoading,
    operationError
  } = useProjectStore();
  
  const { users, fetchUsers, isLoading: isLoadingUsers } = useUserStore();
  
  // Custom hooks
  const [ConfirmDialogComponent, confirmAction] = useConfirmDialog();
  
  const {
    dialogState,
    openAddDialog,
    openEditDialog,
    closeDialog,
    handleSave,
    updateMember
  } = useTeamMemberDialog(id, { 
    addProjectTeamMember, 
    updateProjectTeamMember 
  });
  
  // Load users data
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);
  
  // Handle remove member
  const handleRemoveMember = async (member) => {
    // Handle case where user data might be null/undefined (deleted user)
    const getUserName = () => {
      if (!member?.user) return 'Unknown User';
      const firstName = member.user.firstName || '';
      const lastName = member.user.lastName || '';
      const fullName = `${firstName} ${lastName}`.trim();
      return fullName || 'Unknown User';
    };
    
    const confirmed = await confirmAction({
      title: 'Remove Team Member',
      content: `Are you sure you want to remove ${getUserName()} from this project?`,
      confirmText: 'Remove',
      cancelText: 'Cancel'
    });
    
    if (confirmed) {
      try {
        await removeProjectTeamMember(id, member._id);
      } catch (err) {
        console.log('Failed to remove team member:', err);
      }
    }
  };
  
  // Filter out users already in the team
  const getAvailableUsers = () => {
    if (!project || !users) return [];
    
    const teamUserIds = project.team?.map(member => member.user?._id) || [];
    return users.filter(user => !teamUserIds.includes(user._id));
  };
  
  // Loading state
  if (isLoading && isLoadingUsers) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Don't block UI on error - errors will be shown as notifications
  
  // If project not found
  if (!project) {
    return (
      <EmptyState
        title="Project not found"
        description="The project you're looking for doesn't exist or you don't have access to it."
        action={
          <Button 
            variant="contained" 
            component={Link}
            to="/projects"
          >
            Back to Projects
          </Button>
        }
      />
    );
  }
  
  return (
    <Box>
      {/* Show operation errors inline */}
      {operationError && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          onClose={() => useProjectStore.getState().clearOperationError()}
        >
          {operationError}
        </Alert>
      )}
      
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Project Team
        </Typography>
        
        {canEdit && (
          <Button
            variant="contained"
            
            startIcon={<AddIcon />}
            onClick={openAddDialog}
          >
            Add Team Member
          </Button>
        )}
      </Box>
      
      {/* Team Members List */}
      <TeamMembersList 
        team={project.team}
        canEdit={canEdit}
        onAddClick={openAddDialog}
        onEditClick={openEditDialog}
        onRemoveClick={handleRemoveMember}
        currentUserId={user.id}
      />
      
      {/* Team Member Dialog (Add/Edit) */}
      <TeamMemberDialog
        open={dialogState.isOpen}
        onClose={closeDialog}
        member={dialogState.member}
        onMemberChange={updateMember}
        onSave={handleSave}
        availableUsers={getAvailableUsers()}
        isLoading={isLoading}
        mode={dialogState.mode}
      />
      
      {/* Confirm Dialog */}
      <ConfirmDialogComponent />
    </Box>
  );
};
export default ProjectTeam;